import React from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, Control, UseFormSetValue } from 'react-hook-form'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Copy, Bell } from 'lucide-react'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import DepartmentSelectMutiple from '@/components/Form/Shared/DepartmentSelectMutiple'
import { AssetFormData } from '..'

interface NotificationRecipientsSectionProps {
    control: Control<any>
    setValue: UseFormSetValue<any>
    notifyScope: 'none' | 'all' | 'custom'
    shareScope: 'public' | 'private'
    sharedEmployeeIds: number[]
    sharedDepartmentIds: number[]
    formData: AssetFormData
    setFormData: React.Dispatch<React.SetStateAction<AssetFormData>>
}

const NotificationRecipientsSection: React.FC<
    NotificationRecipientsSectionProps
> = ({
    control,
    setValue,
    notifyScope,
    shareScope,
    sharedEmployeeIds,
    sharedDepartmentIds,
    formData,
    setFormData,
}) => {
    const { t } = useTranslation('asset')

    const handleCopyFromSharing = () => {
        if (shareScope === 'public') {
            setValue('notify_employee_ids', [])
            setValue('notify_department_ids', [])
            setValue('notifyScope', 'all')
            return
        }

        setValue('notify_employee_ids', sharedEmployeeIds || [])
        setValue('notify_department_ids', sharedDepartmentIds || [])
        setValue('notifyScope', 'custom')
    }

    return (
        <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-4 space-y-4">
            <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-emerald-700" />
                <h4 className="text-sm font-medium text-emerald-900">
                    {t('form.notificationRecipients')}
                </h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="notifyScope">{t('form.notifyScope')}</Label>
                    <Controller
                        control={control}
                        name="notifyScope"
                        render={({ field }) => (
                            <Select
                                value={field.value}
                                onValueChange={field.onChange}
                            >
                                <SelectTrigger id="notifyScope">
                                    <SelectValue
                                        placeholder={t('form.notifyScope')}
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">
                                        {t('form.notifyNone')}
                                    </SelectItem>
                                    <SelectItem value="all">
                                        {t('form.notifyAll')}
                                    </SelectItem>
                                    <SelectItem value="custom">
                                        {t('form.notifyCustom')}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                    />
                </div>
            </div>

            {notifyScope === 'custom' && (
                <>
                    <div className="flex justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleCopyFromSharing}
                            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        >
                            <Copy className="h-4 w-4 mr-1" />
                            {t('form.copyFromSharing')}
                        </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <EmployeeSelect
                                control={control}
                                name="notify_employee_ids"
                                multiple
                                label={t('form.notifyEmployees')}
                                onlyActive
                            />
                        </div>
                        <div>
                            <DepartmentSelectMutiple
                                control={control}
                                name="notify_department_ids"
                                multiple
                            />
                        </div>
                    </div>
                </>
            )}

            <div>
                <Label htmlFor="asset-change-note">
                    {t('form.changeNote')}
                </Label>
                <Textarea
                    id="asset-change-note"
                    value={formData.notes}
                    onChange={(e) =>
                        setFormData((prev) => ({
                            ...prev,
                            notes: e.target.value,
                        }))
                    }
                    placeholder={t('form.changeNotePlaceholder')}
                    rows={3}
                    className="mt-1 resize-none"
                />
            </div>
        </div>
    )
}

export default NotificationRecipientsSection
